/* eslint-disable prettier/prettier  */
// React
import React from "react";
import PropTypes from "prop-types";

// Components
import {
  SEGMENT as TEXT_SEGMENT,
  getNormalizedSegment,
} from "@gerenciatd/next13.enums.segments";
import getColor, {
  COLOR_DICTIONARY as TEXT_COLOR,
  OPACITY as TEXT_OPACITY,
} from "@gerenciatd/next13.enums.colors";

// Utils
import { extendTextInfoClassNames, getProperKind } from "./utils";

// Enums
import {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SIZE,
} from "./enums";

export {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_COLOR,
  TEXT_OPACITY,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SEGMENT,
  TEXT_SIZE,
};

const Text = ({ component, ...restProps }) => {
  const Component = component;
  return <Component {...restProps} />;
};

/**
 * @name TextInfo
 * @description atomic component
 * @see {@link https://bit.cloud/gerenciatd/next13/components/text-info}
 * @param {any} props
 * @returns {React.FC}
 */

const COMPONENT_NAME = "TextInfo";

const TextInfo = (props) => {
  const {
    className = "",
    segment: segmentProp,
    kind,
    text,
    color,
    opacity,
    bgOpacity,
    backgroundColor,
    level,
    custoObjectInfo,
    cyData,
    decoration,
    sizeLegacy,
    ...restProps
  } = props;

  const normalizedKind = getProperKind(kind);

  if (!normalizedKind) {
    return null;
  }

  const segment = getNormalizedSegment(segmentProp);
  const colorOpacity = getColor(color, opacity, segment);
  const bgColorOpacity =
    backgroundColor && backgroundColor !== "transparent"
      ? getColor(backgroundColor, bgOpacity, segment)
      : "transparent";
  const extendedClassNames = extendTextInfoClassNames({
    segment,
    kind: normalizedKind,
    decoration,
    sizeLegacy,
    ...restProps,
  });

  const componentClassName = `text${
    className ? ` ${className}` : ""
  } text-color--${segment}--${color.toLowerCase()}${extendedClassNames}${
    custoObjectInfo ? " custo-editable" : ""
  }`;

  return (
    <>
      <Text
        component={
          normalizedKind === TEXT_KIND.HEADING
            ? `${normalizedKind}${level}`
            : normalizedKind
        }
        className={componentClassName}
        dangerouslySetInnerHTML={{ __html: text }}
        onClick={
          custoObjectInfo
            ? () => custoObjectInfo.handler(custoObjectInfo.entryPoint)
            : null
        }
        data-cy={cyData}
        style={{ color: colorOpacity, backgroundColor: bgColorOpacity }}
      />
      <style jsx global>{`.text {
  overflow-wrap: break-word;
  text-wrap: pretty;
  &-gp {
    /* FONTS */
    &--font-regular {
      font-family: Regular;
      font-weight: 400;
    }
    &--font-light {
      font-family: Light;
      font-weight: 300;
    }
    &--font-bold {
      font-family: Bold;
      font-weight: 400;
    }
    &--font-thin {
      font-family: Thin;
      font-weight: 200;
    }
    &--font-hand {
      font-family: Hand;
    }
  }
  &-emp {
    &--font-regular {
      font-family: RegularEmp;
      font-weight: 400;
    }
    &--font-light {
      font-family: LightEmp;
      font-weight: 300;
    }
    &--font-bold {
      font-family: BoldEmp;
      font-weight: 400;
    }
    &--font-thin {
      font-family: ThinEmp;
      font-weight: 200;
    }
    &--font-hand {
      font-family: HandEmp;
    }
  }
  &-plus {
    &--font-regular {
      font-family: RegularPlus;
      font-weight: 500;
    }
    &--font-light {
      font-family: LightPlus;
      font-weight: 500;
    }
    &--font-bold {
      font-family: BoldPlus;
      font-weight: 500;
    }
  }

  /* SIZE*/
  &-size {
    &--display01 { font-size: clamp(3.5rem, 3.5vw, 4rem);       line-height: 100%;}
    &--display02 { font-size: clamp(2.125rem,2.5vw,2.375rem);     line-height: 100%;}
    &--display03 { font-size: clamp(1.5rem, 2.5vw, 2.75rem);    line-height: 100% ;}
    &--title01 {   font-size: clamp(2rem, 2.5vw, 2.5rem);      line-height: 100% ;}
    &--title015 {  font-size: clamp(1.5rem, 2.5vw, 2.5rem);       line-height: 100% ;}
    &--title02 {   font-size: clamp(2.125rem,2.5vw,2.375rem);   line-height: 100% ;}
    &--title03 {   font-size: clamp(1.8rem,2.5vw,2.125rem);   line-height: 100% ;}
    &--title035 {  font-size: clamp(1.1475rem, 2.5vw, 1.185rem); line-height: 120% ;}
    &--title04 {   font-size: clamp(1.25rem, 2.5vw, 1.75rem);     line-height: 120% ;} //titulo ficha
    &--title045 {  font-size: clamp(1.25rem, 2.5vw, 1.375rem); line-height: 120% ;}
    &--title05 {   font-size: clamp(1.25rem, 2.5vw, 1.25rem); line-height: 120% ;}
    &--title06 {   font-size: clamp(1.1rem, 2.5vw, 1.125rem); line-height: 120% ;}
    &--caption01 { font-size: clamp(1rem, 2.5vw, 1rem); line-height: 100% ;}
    &--caption02 { font-size: clamp(0.75rem, 2.5vw, 0.875rem); line-height: 120% ;}
    &--caption03 { font-size: clamp(0.75rem, 2.5vw, 0.75rem); line-height: 120% ;}
  }
  &-size_legacy{
    &--display01 { font-size: clamp(2.5rem, 3.5vw, 4rem); line-height: 120%; }
    &--display02 { font-size: clamp(2rem, 2.5vw, 3.375rem); line-height: 120%; }
    &--display03 { font-size: clamp(1.5rem, 2.5vw, 2.75rem); line-height: 120%; }
    &--title01 { font-size: clamp(1.75rem, 2.5vw, 2.5rem); line-height: 120%; }
    &--title015 { font-size: clamp(1.5rem, 2.5vw, 2.5rem); line-height: 120%; }
    &--title02 { font-size: clamp(1.375rem, 2.5vw, 2.375rem); line-height: 120%; }
    &--title03 { font-size: clamp(1.185rem, 2.5vw, 2.125rem); line-height: 120%;}
    &--title035 { font-size: clamp(1.1475rem, 2.5vw, 1.185rem); line-height: 120%;}
    &--title04 { font-size: clamp(1.115rem, 2.5vw, 1.75rem); line-height: 120%;}
    &--title045 { font-size: clamp(1.0625rem, 2.5vw, 1.375rem); line-height: 120%;}
    &--title05 { font-size: clamp(0.9375rem, 2.5vw, 1.25rem); line-height: 120%;}
    &--title06 { font-size: clamp(0.8875rem, 2.5vw, 1.125rem); line-height: 120%;}
    &--caption01 { font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 120%;}
    &--caption02 { font-size: clamp(0.75rem, 2.5vw, 0.875rem);line-height: 120% ;}
    &--caption03 {font-size: clamp(0.65rem, 2.5vw, 0.75rem);line-height: 120% ;}
  }

  /* ALIGNMENTS */
  &-align {
    &--center {
      text-align: center;
    }
    &--left {
      text-align: left;
    }
    &--right {
      text-align: right;
    }
    &--justify {
      text-align: justify;
    }
  }

  /* DISPLAY */
  &-display {
    &--inline {
      display: inline;
    }
    &--block {
      display: block;
    }
  }

  /* WRAP */
  &-wrap {
    &--pretty {
      text-wrap: pretty;
    }
    &--balance {
      text-wrap: balance;
    }
    &--wrap {
      text-wrap: wrap;
    }
    &--nowrap {
      text-wrap: nowrap;
    }
  }

  /* BORDER */
  &-border--rounded {
    border-radius: 3.125rem;
    padding: 0.25rem 0.75rem;
  }

  /* DECORATION */
  &-decoration {
    &--underline {
      text-decoration: underline;
    }
    &--line-through {
      text-decoration: line-through;
    }
  }
}`}</style>
    </>
  );
};

TextInfo.propTypes = {
  /**
   * Texto que se mostrará en el componente.
   */
  text: PropTypes.string.isRequired,
  /**
   * TEXT_KIND -> Decidirá si el componente es un heading, paragraph o span.
   */
  kind: PropTypes.string,
  /**
   * TEXT_HEADING_LEVEL -> Si el componente es un heading, se especifica el nivel de este (h1, h2, h3, etc).
   */
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Clase para añadirle estilos extras al icono.
   */
  className: PropTypes.string,
  /**
   * TEXT_COLOR -> Color del texto.
   */
  color: PropTypes.string,
  /**
   * TEXT_SIZE -> Tamaño del texto. Se elegirá entre Title, Display o Caption (cada uno con sus niveles correspondientes).
   */
  size: PropTypes.string,
  /**
   * TEXT_FONT_WEIGHT -> Grosor del texto (Regular, Light o Bold).
   */
  fontType: PropTypes.string,
  /**
   * TEXT_ALIGN -> Alineación del texto (centrado, a la izquierda/derecha o justificado).
   */
  align: PropTypes.string,
  /**
   * TEXT_DISPLAY -> Especifica si el texto es tratado como un elemento block o inline.
   */
  display: PropTypes.string,
  /**
   * TEXT_WRAP -> Especifica si el texto se corta para no dejar solo una palabra en la última línea (pretty),
   * o se balanceara en dos líneas del mismo tamaño(balance),
   * además permite wrap y nowrap.
   */
  wrap: PropTypes.string,
  /**
   * TEXT_DECORATION -> Si el texto tendrá subrayado o una línea cruzada.
   */
  decoration: PropTypes.string,
  /**
   * TEXT_BORDER -> Redondeado del texto.
   */
  border: PropTypes.string,
  /**
   * TEXT_COLOR -> Color del fondo del texto.
   */
  backgroundColor: PropTypes.string,
  /**
   * TEXT_OPACITY -> Opacidad del texto.
   */
  opacity: PropTypes.string,
  /**
   * TEXT_OPACITY -> Opacidad del fondo.
   */
  bgOpacity: PropTypes.string,
  /**
   * Objeto para la customización del icono en dev.
   */
  custoObjectInfo: PropTypes.shape({
    componentId: PropTypes.string,
    componentInstance: PropTypes.string,
    editFunction: PropTypes.func,
  }),
  cyData: PropTypes.string,
  sizeLegacy: PropTypes.bool,
};

TextInfo.defaultProps = {
  kind: TEXT_KIND.SPAN,
  level: TEXT_HEADING_LEVEL.H1,
  className: "",
  color: TEXT_COLOR.BLACK,
  size: TEXT_SIZE.CAPTION2,
  fontType: TEXT_FONT_WEIGHT.REGULAR,
  custoObjectInfo: null,
  align: TEXT_ALIGN.LEFT,
  border: TEXT_BORDER.NONE,
  decoration: TEXT_DECORATION.NONE,
  display: TEXT_DISPLAY.BLOCK,
  wrap: TEXT_WRAP.PRETTY,
  backgroundColor: TEXT_COLOR.TRANSPARENT,
  opacity: TEXT_OPACITY[100],
  bgOpacity: TEXT_OPACITY[100],
  sizeLegacy: false,
  cyData: "",
};

export default TextInfo;
